import React, { useState, useEffect } from 'react';
import { 
  User, 
  Shield, 
  Database, 
  Bell, 
  Palette,
  Key,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  Download,
  Upload,
  Lock,
  AlertTriangle,
  CheckCircle,
  X
} from 'lucide-react';
import MFASettings from './MFASettings';
import { useUserSettings } from '../hooks/useUserSettings';

interface SettingsManagerProps {}

// Types simplifiés pour les paramètres

interface UserPreferences {
  theme: 'dark' | 'light' | 'auto';
  language: 'fr' | 'en' | 'es';
  fontSize: 'small' | 'medium' | 'large';
  compactMode: boolean;
  showAnimations: boolean;
  autoSave: boolean;
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  newEnqueteAlert: boolean;
  deadlineReminders: boolean;
  weeklyReports: boolean;
  securityAlerts: boolean;
  twoFactorEnabled: boolean;
  sessionTimeout: number;
  maxLoginAttempts: number;
  passwordComplexity: boolean;
  autoLogout: boolean;
  autoBackup: boolean;
  backupFrequency: 'hourly' | 'daily' | 'weekly' | 'monthly';
  retentionPeriod: number;
  exportFormat: 'pdf' | 'json' | 'csv' | 'xml';
  dataEncryption: boolean;
  cloudSync: boolean;
  apiRateLimit: number;
}

const SettingsManager: React.FC<SettingsManagerProps> = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Utiliser le hook pour les paramètres utilisateur
  const {
    profile,
    preferences,
    profileLoading,
    preferencesLoading,
    profileError,
    preferencesError,
    updateProfile,
    changePassword,
    updatePreferences,
    profileUpdateSuccess,
    passwordChangeSuccess,
    preferencesUpdateSuccess,
    clearProfileUpdateSuccess,
    clearPasswordChangeSuccess,
    clearPreferencesUpdateSuccess
  } = useUserSettings();

  // États locaux pour les formulaires
  const [profileForm, setProfileForm] = useState({
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    email: profile?.email || ''
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [apiKeys, setApiKeys] = useState({
    osintApiKey: '',
    mappingApiKey: '',
    socialMediaApiKey: '',
    databaseApiKey: '',
    webhookUrl: '',
    apiRateLimit: 1000
  });

  // Mettre à jour le formulaire de profil quand les données sont chargées
  React.useEffect(() => {
    if (profile) {
      setProfileForm({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || ''
      });
    }
  }, [profile]);

  // Fonctions de sauvegarde avec vrais appels API
  const handleSaveProfile = async () => {
    try {
      await updateProfile({
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        email: profileForm.email
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du profil:', error);
    }
  };

  const handleChangePassword = async () => {
    try {
      console.log('🔍 Validation des mots de passe:', {
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
        match: passwordForm.newPassword === passwordForm.confirmPassword
      });
      
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        throw new Error('Les mots de passe ne correspondent pas');
      }
      
      if (passwordForm.newPassword.length < 6) {
        throw new Error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      }
      
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      
      // Réinitialiser le formulaire en cas de succès
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
    }
  };

  const handleSavePreferences = async (section: string, newPrefs: Partial<UserPreferences>) => {
    try {
      await updatePreferences(newPrefs);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des préférences:', error);
    }
  };

  // Fonction pour sauvegarder les clés API (pas encore implémentée)
  /*
  const handleSaveApiKeys = async () => {
    try {
      setSectionLoading('api');
      // Simuler un appel API
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccessMessage('Clés API sauvegardées');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Erreur lors de la sauvegarde des clés API');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setSectionLoading('');
    }
  };
  */

  const handleExportData = async () => {
    try {
      // Simuler l'export
      await new Promise(resolve => setTimeout(resolve, 1000));
      const data = { profile, preferences, apiKeys };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `osint-data-export.${preferences?.exportFormat || 'json'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
    }
  };

  const handleImportData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.csv,.xml';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          // Simuler l'import
          await new Promise(resolve => setTimeout(resolve, 1000));
          console.log('Données importées avec succès');
        } catch (error) {
          console.error('Erreur lors de l\'import:', error);
        }
      }
    };
    input.click();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleResetSettings = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser tous les paramètres ? Cette action est irréversible.')) {
      try {
        // Réinitialiser aux valeurs par défaut
        const defaultPreferences = {
          theme: 'dark' as const,
          language: 'fr' as const,
          fontSize: 'medium' as const,
          compactMode: false,
          showAnimations: true,
          autoSave: true,
          emailNotifications: true,
          smsNotifications: false,
          pushNotifications: true,
          newEnqueteAlert: true,
          deadlineReminders: true,
          weeklyReports: false,
          securityAlerts: true,
          twoFactorEnabled: false,
          sessionTimeout: 30,
          maxLoginAttempts: 5,
          passwordComplexity: true,
          autoLogout: true,
          autoBackup: true,
          backupFrequency: 'daily' as const,
          retentionPeriod: 365,
          exportFormat: 'pdf' as const,
          dataEncryption: true,
          cloudSync: false,
          apiRateLimit: 1000
        };
        
        setApiKeys({
          osintApiKey: '',
          mappingApiKey: '',
          socialMediaApiKey: '',
          databaseApiKey: '',
          webhookUrl: '',
          apiRateLimit: 1000
        });
        
        await updatePreferences(defaultPreferences);
        console.log('Paramètres réinitialisés');
      } catch (error) {
        console.error('Erreur lors de la réinitialisation:', error);
      }
    }
  };

  // Initialiser le formulaire profil
  useEffect(() => {
    if (profile) {
      setProfileForm({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || ''
      });
    }
  }, [profile]);

  const tabs = [
    { id: 'profile', name: 'Profil', icon: User },
    { id: 'security', name: 'Sécurité', icon: Shield },
    { id: 'mfa', name: 'Authentification 2FA', icon: Lock },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'appearance', name: 'Apparence', icon: Palette },
    { id: 'data', name: 'Données', icon: Database },
    { id: 'api', name: 'API', icon: Key }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Paramètres</h2>
          <p className="text-gray-400">Configuration du système et préférences</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleExportData}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white text-sm transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Exporter</span>
          </button>
          <button
            onClick={handleImportData}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg text-white text-sm transition-colors"
          >
            <Upload className="w-4 h-4" />
            <span>Importer</span>
          </button>
        </div>
      </div>

      {/* Messages de succès/erreur globaux */}
      {(profileUpdateSuccess || passwordChangeSuccess || preferencesUpdateSuccess) && (
        <div className="flex items-center space-x-2 p-3 bg-green-600/20 border border-green-600/50 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-300">
            {profileUpdateSuccess && 'Profil mis à jour avec succès'}
            {passwordChangeSuccess && 'Mot de passe modifié avec succès'}
            {preferencesUpdateSuccess && 'Préférences mises à jour'}
          </span>
          <button 
            onClick={() => {
              clearProfileUpdateSuccess();
              clearPasswordChangeSuccess();
              clearPreferencesUpdateSuccess();
            }} 
            className="ml-auto text-green-400 hover:text-green-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {(profileError || preferencesError) && (
        <div className="flex items-center space-x-2 p-3 bg-red-600/20 border border-red-600/50 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <span className="text-red-300">{profileError || preferencesError}</span>
          <button 
            onClick={() => {
              // Les erreurs se clearent automatiquement dans le hook
            }} 
            className="ml-auto text-red-400 hover:text-red-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar des onglets */}
        <div className="lg:col-span-1">
          <div className="osint-card p-4 rounded-lg">
            <nav className="space-y-2">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-osint-orange text-white'
                        : 'text-gray-300 hover:bg-osint-light-gray'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="lg:col-span-3">
          <div className="osint-card p-6 rounded-lg">
            {/* Onglet Profil */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Profil Utilisateur</h3>
                  <button
                    onClick={handleSaveProfile}
                    disabled={profileLoading}
                    className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 disabled:opacity-50 rounded-lg text-white text-sm transition-colors"
                  >
                    {profileLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Sauvegarde...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Sauvegarder</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Prénom</label>
                    <input
                      type="text"
                      value={profileForm.firstName}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
                      className="osint-input w-full px-3 py-2 rounded-lg text-white"
                      placeholder="Entrez votre prénom"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Nom</label>
                    <input
                      type="text"
                      value={profileForm.lastName}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
                      className="osint-input w-full px-3 py-2 rounded-lg text-white"
                      placeholder="Entrez votre nom"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                    <input
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                      className="osint-input w-full px-3 py-2 rounded-lg text-white"
                      placeholder="Entrez votre email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Rôle</label>
                    <input
                      type="text"
                      value={profile?.role || ''}
                      className="osint-input w-full px-3 py-2 rounded-lg text-white bg-gray-600"
                      disabled
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Compte créé le</label>
                    <input
                      type="text"
                      value={profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString('fr-FR') : ''}
                      className="osint-input w-full px-3 py-2 rounded-lg text-white bg-gray-600"
                      disabled
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Onglet Sécurité */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Sécurité</h3>
                  <button
                    onClick={handleChangePassword}
                    disabled={profileLoading || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                    className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 disabled:opacity-50 rounded-lg text-white text-sm transition-colors"
                  >
                    {profileLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Modification...</span>
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4" />
                        <span>Changer le mot de passe</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Messages d'erreur et succès */}
                  {profileError && (
                    <div className="flex items-center space-x-2 p-3 bg-red-900/20 border border-red-500/50 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      <span className="text-red-400">{profileError}</span>
                    </div>
                  )}
                  
                  {passwordChangeSuccess && (
                    <div className="flex items-center space-x-2 p-3 bg-green-900/20 border border-green-500/50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-green-400">Mot de passe changé avec succès</span>
                    </div>
                  )}

                  {/* Changement de mot de passe */}
                  <div className="p-4 bg-osint-light-gray rounded-lg">
                    <h4 className="text-white font-medium mb-4">Changement de mot de passe</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Mot de passe actuel</label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                            className="osint-input w-full px-3 py-2 pr-10 rounded-lg text-white"
                          />
                          <button
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Nouveau mot de passe</label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                            className="osint-input w-full px-3 py-2 pr-10 rounded-lg text-white"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Confirmer le nouveau mot de passe</label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            className="osint-input w-full px-3 py-2 pr-10 rounded-lg text-white"
                          />
                          <button
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Options de sécurité */}
                  <div className="p-4 bg-osint-light-gray rounded-lg">
                    <h4 className="text-white font-medium mb-4">Options de sécurité</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">Authentification à deux facteurs</p>
                          <p className="text-gray-400 text-sm">Sécurisez votre compte avec 2FA</p>
                        </div>
                        <button
                          onClick={() => handleSavePreferences('security', { twoFactorEnabled: !preferences?.twoFactorEnabled })}
                          disabled={preferencesLoading}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            preferences?.twoFactorEnabled ? 'bg-osint-orange' : 'bg-gray-600'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences?.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">Déconnexion automatique</p>
                          <p className="text-gray-400 text-sm">Se déconnecter après inactivité</p>
                        </div>
                        <button
                          onClick={() => handleSavePreferences('security', { autoLogout: !preferences?.autoLogout })}
                          disabled={preferencesLoading}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            preferences?.autoLogout ? 'bg-osint-orange' : 'bg-gray-600'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences?.autoLogout ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">Complexité du mot de passe</p>
                          <p className="text-gray-400 text-sm">Exiger des mots de passe complexes</p>
                        </div>
                        <button
                          onClick={() => handleSavePreferences('security', { passwordComplexity: !preferences?.passwordComplexity })}
                          disabled={preferencesLoading}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            preferences?.passwordComplexity ? 'bg-osint-orange' : 'bg-gray-600'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences?.passwordComplexity ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Onglet MFA */}
            {activeTab === 'mfa' && (
              <MFASettings />
            )}

            {/* Les autres onglets continueront avec un code similaire simplifié... */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Notifications</h3>
                  <div className="text-sm text-gray-400">
                    Les notifications sont sauvegardées automatiquement
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Exemple de notification */}
                  <div className="flex items-center justify-between p-4 bg-osint-light-gray rounded-lg">
                    <div>
                      <p className="text-white font-medium">Notifications par email</p>
                      <p className="text-gray-400 text-sm">Recevoir les notifications par email</p>
                    </div>
                    <button
                      onClick={() => handleSavePreferences('notifications', { emailNotifications: !preferences?.emailNotifications })}
                      disabled={preferencesLoading}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        preferences?.emailNotifications ? 'bg-osint-orange' : 'bg-gray-600'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences?.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  {/* Ajouter les autres notifications... */}
                </div>
              </div>
            )}

            {/* Autres onglets... pour l'instant affichage simple */}
            {(activeTab === 'appearance' || activeTab === 'data' || activeTab === 'api') && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">
                    {activeTab === 'appearance' && 'Apparence'}
                    {activeTab === 'data' && 'Gestion des données'}
                    {activeTab === 'api' && 'Configuration API'}
                  </h3>
                </div>
                <div className="p-8 text-center text-gray-400">
                  <p>Cette section sera implémentée prochainement.</p>
                  <p className="text-sm mt-2">En attendant, les fonctionnalités de base sont disponibles dans les autres onglets.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default SettingsManager;